/**
 * Shared constants and types for the Add User wizard (`pages/InviteUser.tsx`).
 */
import type { UserSitePermissionFlags } from '../../lib/api';
import { notifyDefaultsFor, type NotifyFlags } from '../../lib/departments';
import { User as UserIcon, Building2, ShieldCheck, ClipboardList } from 'lucide-react';

export type WizardStep = 1 | 2 | 3 | 4;

/** Every per-site flag the wizard sets, all required. */
export type PermissionFlags = Required<UserSitePermissionFlags>;

export const STEPS: { num: WizardStep; label: string; icon: typeof UserIcon }[] = [
  { num: 1, label: 'Profile', icon: UserIcon },
  { num: 2, label: 'Companies', icon: Building2 },
  { num: 3, label: 'Access', icon: ShieldCheck },
  { num: 4, label: 'Review', icon: ClipboardList },
];

export const SALUTATIONS = ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Prof'];

export const DEFAULT_PERMISSION_FLAGS: PermissionFlags = {
  can_purchasing: true,
  can_quality: true,
  // CR-8252: compliance is a real module permission now; on by default like
  // Quality, so an admin unticks it for whoever should not have it.
  can_compliance: true,
  is_admin: false,
  can_approve: false,
  is_read_only: false,
  notify_procurement: true,
  notify_quality: true,
  notify_compliance: false,
};

/** Helper copy under the "Documents" notification switch (notify_compliance). */
export const DOCUMENTS_NOTIFY_HINT = 'Supplier document uploads and Read & Accept requests';

export interface ProfileFields {
  salutation: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  job_title: string;
  /** Stored department value (`lib/departments` DEPARTMENTS), '' when none. */
  department: string;
  message: string;
  role: number | null;
}

export const EMPTY_PROFILE: ProfileFields = {
  salutation: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  job_title: '',
  department: '',
  message: '',
  role: null,
};

/**
 * Overlay a department's starting notification switches onto a flag set.
 *
 * Only the three `notify_*` flags change; modules and the read-only override
 * are kept as the admin left them.
 *
 * @param flags - the current per-site flags.
 * @param department - stored department value ('' for none).
 * @returns A new flag set with the department's notification defaults.
 */
export function withDepartmentNotifyDefaults(flags: PermissionFlags, department: string): PermissionFlags {
  return { ...flags, ...notifyDefaultsFor(department) };
}

const NOTIFY_KEYS: (keyof NotifyFlags)[] = ['notify_procurement', 'notify_quality', 'notify_compliance'];

/**
 * Move a flag set from one department's notification defaults to another's.
 *
 * A switch still at the old department's default follows the new department;
 * one the admin changed by hand keeps the value they chose.
 *
 * @param flags - the current per-site flags.
 * @param fromDepartment - department the flags were last filled from ('' for none).
 * @param toDepartment - newly picked department ('' for none).
 * @returns A new flag set.
 */
export function refillDepartmentNotifyDefaults(
  flags: PermissionFlags, fromDepartment: string, toDepartment: string,
): PermissionFlags {
  const from = notifyDefaultsFor(fromDepartment);
  const to = notifyDefaultsFor(toDepartment);
  const next = { ...flags };
  for (const key of NOTIFY_KEYS) {
    if (flags[key] === from[key]) next[key] = to[key];
  }
  return next;
}
